import type { LucideIcon } from "lucide-react";
import {
    ClipboardList,
    ListChecks,
    MessagesSquare,
    ScanSearch,
} from "lucide-react";
import { DataKeys } from "../../../types";
import { useLang } from "../../Context";
import { StaggerInView, StaggerInViewItem } from "../../tools/Animations";
import { HealthSection } from "../HealthSection";
import { HealthSectionHeader } from "../HealthSectionHeader";
import { getLocalized, HealthLang, LocalizedString } from "../healthTypes";
import { healthAimmaDoctorSectionContentOffset } from "../healthLayout";
import {
    HEALTH_BG_GREY,
    healthSoapCardBadge,
    healthSoapCardBase,
    healthSoapCardContent,
    healthSoapCardDivider,
    healthSoapCardGrid,
    healthSoapCardIcon,
    healthSoapCardIconWrap,
    healthSoapCardText,
    healthSoapCardThemeAssessment,
    healthSoapCardThemeObjective,
    healthSoapCardThemePlan,
    healthSoapCardThemeSubjective,
    healthSoapCardTitle,
} from "../healthVisualStyles";

interface HealthSoapWorkflowSectionProps {
    data: DataKeys;
}

type SoapCardId = "subjective" | "objective" | "assessment" | "plan";

type SoapCardTheme = {
    card: string;
    badge: string;
    title: string;
    divider: string;
    icon: string;
};

const SOAP_CARD_THEMES: Record<SoapCardId, SoapCardTheme> = {
    subjective: healthSoapCardThemeSubjective,
    objective: healthSoapCardThemeObjective,
    assessment: healthSoapCardThemeAssessment,
    plan: healthSoapCardThemePlan,
};

const SOAP_CARD_ICONS: Record<SoapCardId, LucideIcon> = {
    subjective: MessagesSquare,
    objective: ClipboardList,
    assessment: ScanSearch,
    plan: ListChecks,
};

const ICON_STROKE = 1.75;

function isSoapCardId(value: string): value is SoapCardId {
    return value in SOAP_CARD_THEMES;
}

export function HealthSoapWorkflowSection({ data }: HealthSoapWorkflowSectionProps) {
    const { lang } = useLang();
    const hLang = lang as HealthLang;
    const cards = (data.cards as DataKeys[]) ?? [];

    return (
        <HealthSection
            id="soap-workflow"
            ariaLabelledBy="soap-workflow-title"
            compact
            style={{ backgroundColor: HEALTH_BG_GREY }}
        >
            <HealthSectionHeader
                eyebrow={getLocalized(data.eyebrow as LocalizedString, hLang)}
                title={getLocalized(data.title as LocalizedString, hLang)}
                titleId="soap-workflow-title"
                subtitle={getLocalized(data.intro as LocalizedString, hLang)}
                subtitleWide
            />

            <div className={healthAimmaDoctorSectionContentOffset}>
                <StaggerInView className={healthSoapCardGrid}>
                    {cards.map((card, index) => {
                        const cardId = card.id as string;
                        const theme = isSoapCardId(cardId)
                            ? SOAP_CARD_THEMES[cardId]
                            : healthSoapCardThemeSubjective;
                        const Icon = isSoapCardId(cardId)
                            ? SOAP_CARD_ICONS[cardId]
                            : MessagesSquare;

                        return (
                            <StaggerInViewItem key={cardId} direction="bottom">
                            <article
                                className={`${healthSoapCardBase} ${theme.card}`}
                            >
                                <div className={healthSoapCardContent}>
                                    <div
                                        className={`${healthSoapCardBadge} ${theme.badge}`}
                                        aria-hidden="true"
                                    >
                                        {card.letter as string}
                                    </div>
                                    <h3
                                        className={`${healthSoapCardTitle} ${theme.title}`}
                                    >
                                        {getLocalized(
                                            card.title as LocalizedString,
                                            hLang
                                        )}
                                    </h3>
                                    <div
                                        className={`${healthSoapCardDivider} ${theme.divider}`}
                                        aria-hidden="true"
                                    />
                                    <p className={healthSoapCardText}>
                                        {getLocalized(
                                            card.text as LocalizedString,
                                            hLang
                                        )}
                                    </p>
                                </div>
                                <div className={healthSoapCardIconWrap}>
                                    <Icon
                                        className={`${healthSoapCardIcon} ${theme.icon}`}
                                        strokeWidth={1.5}
                                        aria-hidden="true"
                                    />
                                </div>
                            </article>
                            </StaggerInViewItem>
                        );
                    })}
                </StaggerInView>
            </div>
        </HealthSection>
    );
}
